import { useEffect, useState } from "react";
import {
  X,
  Loader2,
  MessageSquare,
  Calendar,
  ArrowRight,
  BriefcaseBusiness,
} from "lucide-react";

const DealActionDrawer = ({
  open,
  onClose,
  deal,
  action,
  onSubmit,
  loading = false,
}) => {
  const [remark, setRemark] = useState("");
  const [interviewDate, setInterviewDate] = useState("");
  const [joiningDate, setJoiningDate] = useState("");
  const [offeredCTC, setOfferedCTC] = useState("");
  const [expectedJoiningDate, setExpectedJoiningDate] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const isBusy = loading || submitting;

  useEffect(() => {
    if (open) {
      setRemark("");
      setInterviewDate("");
      setJoiningDate("");
      setOfferedCTC("");
      setExpectedJoiningDate("");
      setErrors({});
    }
  }, [open, action]);

  const handleClose = () => {
    if (isBusy) return;
    onClose();
  };

  const handleSubmit = async () => {
    const newErrors = {};
    const now = new Date();

    if (!remark.trim()) {
      newErrors.remark = "Remark is required";
    }

    // Schedule Interview
    if (action === "Schedule Interview") {
      if (!interviewDate) {
        newErrors.interviewDate = "Interview date is required";
      } else if (new Date(interviewDate) < now) {
        newErrors.interviewDate = "Interview date must be in the future";
      }
    }

    // Release Offer
    if (action === "Release Offer") {
      const offeredCTCValue = Number(offeredCTC);

      if (!offeredCTC) {
        newErrors.offeredCTC = "Offered CTC is required";
      } else if (Number.isNaN(offeredCTCValue) || offeredCTCValue <= 0) {
        newErrors.offeredCTC = "Enter a valid positive CTC amount";
      }

      if (!expectedJoiningDate) {
        newErrors.expectedJoiningDate = "Expected joining date is required";
      } else if (
        new Date(`${expectedJoiningDate}T00:00`) <
        new Date(
          `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T00:00`,
        )
      ) {
        newErrors.expectedJoiningDate =
          "Expected joining date cannot be in the past";
      }
    }

    // Hire
    if (action === "Hire") {
      if (!joiningDate) {
        newErrors.joiningDate = "Joining date is required";
      } else if (
        new Date(`${joiningDate}T00:00`) <
        new Date(
          `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}T00:00`,
        )
      ) {
        newErrors.joiningDate = "Joining date cannot be in the past";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);

    let payload = {
      dealUID: deal?.dealUID,
      remark,
    };

    switch (action) {
      case "Schedule Interview":
        payload = {
          ...payload,
          interviewDate,
        };
        break;

      case "Release Offer":
        payload = {
          ...payload,
          offeredCTC,
          expectedJoiningDate,
        };
        break;

      case "Hire":
        payload = {
          ...payload,
          joiningDate,
        };
        break;

      default:
        break;
    }

    try {
      const result = onSubmit(payload);
      if (result && typeof result.then === "function") {
        await result;
      }
    } finally {
      setSubmitting(false);
    }
  };

  const requiresInterviewDate = action === "Schedule Interview";

  const requiresHireFields = action === "Hire";

  const requiresReleaseOfferFields = action === "Release Offer";

  return (
    <div
      className={`fixed inset-0 z-50 transition-all duration-300 ${
        open ? "visible" : "invisible"
      }`}
    >
      {/* Overlay */}
      <div
        onClick={handleClose}
        className={`absolute inset-0 bg-slate-800/30 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0"
        }`}
      />

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-[640px] flex flex-col
        bg-white border-l border-slate-200 shadow-2xl
        transition-transform duration-300
        ${open ? "translate-x-0" : "translate-x-full"}
      `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
              <BriefcaseBusiness size={16} className="text-blue-600" />
            </div>

            <div>
              <h2 className="font-bold text-slate-800">{action}</h2>

              <p className="text-xs text-slate-400">
                Fill all required fields before submitting the action.
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center"
          >
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Candidate Card */}
          <div className="bg-white border border-slate-200 rounded-3xl overflow-hidden shadow-sm">
            <div className="bg-[#0577e1] px-5 py-4">
              <h3 className="text-white font-semibold text-lg">Deal Details</h3>
            </div>

            <div className="p-5">
              <div className="flex items-center gap-4 mb-5">
                <div className="h-14 w-14 rounded-full bg-blue-100 text-[#004687] flex items-center justify-center font-bold text-xl">
                  {deal?.name?.charAt(0)}
                </div>

                <div>
                  <h4 className="font-bold text-slate-800 text-lg">
                    {deal?.name}
                  </h4>

                  <p className="text-slate-500 text-sm">{deal?.phoneNo}</p>

                  <p className="text-slate-500 text-sm">{deal?.email || ""}</p>
                </div>
              </div>

              <hr />

              <div className="grid grid-cols-2 gap-4 mt-2">
                <div>
                  <p className="text-xs text-slate-500">Organization</p>

                  <p className="font-medium text-slate-800">
                    {deal?.targetOrganization}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Role</p>

                  <p className="font-medium text-slate-800">
                    {deal?.targetRole}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Status</p>

                  <span className="inline-flex px-3 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                    {deal?.dealStatus}
                  </span>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Source</p>

                  <p className="font-medium text-slate-800">{deal?.source}</p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Owner</p>

                  <p className="font-medium text-slate-800">
                    {deal?.ownerName}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Created By</p>

                  <p className="font-medium text-slate-800">
                    {deal?.createdBy}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Created At</p>

                  <p className="font-medium text-slate-800">
                    {deal?.createdAt}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-slate-500">Last Updated</p>

                  <p className="font-medium text-slate-800">
                    {deal?.lastUpdatedAt || "-"}
                  </p>
                </div>

                {deal?.interviewDate && (
                  <div>
                    <p className="text-xs text-slate-500">Interview Date</p>

                    <p className="font-medium text-slate-800">
                      {deal?.interviewDate}
                    </p>
                  </div>
                )}

                {deal?.expectedJoiningDate && (
                  <div>
                    <p className="text-xs text-slate-500">Expected Joining</p>

                    <p className="font-medium text-slate-800">
                      {deal?.expectedJoiningDate}
                    </p>
                  </div>
                )}

                {deal?.joiningDate && (
                  <div>
                    <p className="text-xs text-slate-500">Joining Date</p>

                    <p className="font-medium text-slate-800">
                      {deal?.joiningDate}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Interview Date */}
          {requiresInterviewDate && (
            <div>
              <label className="text-sm font-semibold text-slate-600 mb-2 block">
                Interview Date <span className="text-red-500">*</span>
              </label>

              <div className="relative">
                <Calendar
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  type="datetime-local"
                  value={interviewDate}
                  onChange={(e) => {
                    setInterviewDate(e.target.value);

                    if (errors.interviewDate) {
                      setErrors((prev) => ({
                        ...prev,
                        interviewDate: "",
                      }));
                    }
                  }}
                  className={`w-full h-11 pl-10 pr-3 rounded-xl border ${errors.interviewDate ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"}`}
                />
                {errors.interviewDate && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.interviewDate}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Hire Fields */}
          {requiresHireFields && (
            <>
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">
                  Joining Date <span className="text-red-500">*</span>
                </label>

                <div className="relative">
                  <Calendar
                    size={14}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />

                  <input
                    type="date"
                    value={joiningDate}
                    onChange={(e) => {
                      setJoiningDate(e.target.value);

                      if (errors.joiningDate) {
                        setErrors((prev) => ({
                          ...prev,
                          joiningDate: "",
                        }));
                      }
                    }}
                    className={`w-full h-11 pl-10 pr-3 rounded-xl border ${errors.joiningDate ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"}`}
                  />
                  {errors.joiningDate && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.joiningDate}
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {requiresReleaseOfferFields && (
            <>
              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">
                  Offered CTC <span className="text-red-500">*</span>
                </label>

                <input
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  value={offeredCTC}
                  onChange={(e) => {
                    setOfferedCTC(e.target.value);
                    if (errors.offeredCTC) {
                      setErrors((prev) => ({
                        ...prev,
                        offeredCTC: "",
                      }));
                    }
                  }}
                  placeholder="Enter CTC"
                  className={`w-full h-11 px-3 rounded-xl border ${errors.offeredCTC ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"}`}
                />
                {errors.offeredCTC && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.offeredCTC}
                  </p>
                )}
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-600 mb-2 block">
                  Expected Joining Date <span className="text-red-500">*</span>
                </label>

                <input
                  type="date"
                  value={expectedJoiningDate}
                  onChange={(e) => {
                    setExpectedJoiningDate(e.target.value);
                    if (errors.expectedJoiningDate) {
                      setErrors((prev) => ({
                        ...prev,
                        expectedJoiningDate: "",
                      }));
                    }
                  }}
                  className={`w-full h-11 px-3 rounded-xl border ${errors.expectedJoiningDate ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"}`}
                />
                {errors.expectedJoiningDate && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.expectedJoiningDate}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Remark */}
          <div>
            <label className="text-sm font-semibold text-slate-600 mb-2 block">
              Remark <span className="text-red-500">*</span>
            </label>

            <div className="relative">
              <MessageSquare
                size={14}
                className="absolute left-3 top-3 text-slate-400"
              />

              <textarea
                rows={5}
                value={remark}
                onChange={(e) => {
                  setRemark(e.target.value);

                  if (errors.remark) {
                    setErrors((prev) => ({
                      ...prev,
                      remark: "",
                    }));
                  }
                }}
                placeholder="Enter remarks..."
                className={`w-full rounded-xl border pl-10 pr-3 py-3 resize-none ${errors.remark ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-200"}`}
              />
              {errors.remark && (
                <p className="text-red-500 text-xs mt-1">{errors.remark}</p>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t px-4 py-3 flex gap-3">
          <button
            onClick={handleClose}
            disabled={loading}
            className="flex-1 h-10 rounded-xl border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={isBusy}
            className="flex-[2] h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center gap-2 hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-70"
          >
            {isBusy ? (
              <>
                <Loader2 size={14} className="animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Confirm
                <ArrowRight size={14} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DealActionDrawer;
